/**
 * Whether an element sits entirely inside its scroll container's viewport.
 *
 * Deliberately strict: a tile cut off at the fold is exactly the one a user
 * would miss, so "partly visible" counts as not visible.
 *
 * Built on offsetTop arithmetic rather than getBoundingClientRect on
 * purpose: rects include in-flight CSS transforms, and the FLIP hook may be
 * animating a sibling (or this very tile) when the toast decision runs.
 * offsetTop is pure layout — transforms cannot corrupt it — which frees the
 * caller from caring about effect ordering.
 *
 * Its own module so tests can substitute it: jsdom reports zero for every
 * offset, which would otherwise read as permanently visible.
 *
 * topInset is the height of anything sticky covering the top of the
 * scrollport — the Deliveries control strip. A tile behind the strip is not
 * visible however favourable the raw scroll numbers look, and calling it
 * visible would suppress the one toast the user needed.
 */
export function isFullyVisible(
  element: HTMLElement,
  scrollerSelector = '.main',
  topInset = 0,
): boolean {
  const scroller = element.closest<HTMLElement>(scrollerSelector)
  if (!scroller) return true

  // Element's top relative to the scroller's content box.
  let top = 0
  let node: HTMLElement | null = element
  while (node && node !== scroller) {
    top += node.offsetTop
    node = node.offsetParent instanceof HTMLElement ? node.offsetParent : null
  }

  const viewTop = scroller.scrollTop + topInset
  const viewBottom = scroller.scrollTop + scroller.clientHeight
  return top >= viewTop && top + element.offsetHeight <= viewBottom
}
